import React, { useEffect, useMemo, useState } from 'react';
import type {
  AvailableClinicianResponse as AvailableClinician,
  PatientCommunicationResponse,
  PatientCommunicationTypeId,
} from '../../services/api/types.gen';
import {
  Button,
  DatePicker,
  DialogFooter,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@roger/ui-components';
import { formatDateToISO, parseISOToLocalDate } from '../../utils/dateUtils';
import { useToast } from '../../hooks/useToastContext';
import {
  PATIENT_COMMUNICATION_TYPES,
  patientCommunicationTypeRequiresAssignableClinician,
} from './patientCommunicationTypes';

export type CommunicationVisitEditorProps = {
  communication: PatientCommunicationResponse;
  assignableClinicians: AvailableClinician[];
  /** User_clinics with is_physician for this clinic (not limited to assignable-clinician). */
  physicianClinicians: AvailableClinician[];
  formatClinicianName: (c: AvailableClinician) => string;
  isSaving?: boolean;
  onSave: (payload: {
    communication_date: string;
    communication_type: PatientCommunicationTypeId;
    assigned_clinician_id: string | null;
    physician_user_clinic_id: string;
    /** Non-empty when validation passes */
    notes: string;
  }) => Promise<void>;
  /** Shown as outline button before Save (e.g. close modal) */
  onCancel?: () => void;
};

const CommunicationVisitEditor: React.FC<CommunicationVisitEditorProps> = ({
  communication,
  assignableClinicians,
  physicianClinicians,
  formatClinicianName,
  isSaving,
  onSave,
  onCancel,
}) => {
  const { error: showError } = useToast();
  const [date, setDate] = useState<Date | undefined>(() =>
    communication.communication_date
      ? parseISOToLocalDate(communication.communication_date)
      : undefined
  );
  const [commType, setCommType] = useState<PatientCommunicationTypeId>(
    communication.communication_type ?? 'Patient Communication'
  );
  const [assignee, setAssignee] = useState<string>(
    communication.assigned_clinician_id ?? ''
  );
  const [physicianId, setPhysicianId] = useState<string>(
    communication.physician_user_clinic_id ?? ''
  );
  const [notes, setNotes] = useState<string>(communication.notes ?? '');

  const physicianOptions = physicianClinicians;

  const needsAssignee =
    patientCommunicationTypeRequiresAssignableClinician(commType);

  useEffect(() => {
    setDate(
      communication.communication_date
        ? parseISOToLocalDate(communication.communication_date)
        : undefined
    );
    setCommType(communication.communication_type ?? 'Patient Communication');
    setAssignee(communication.assigned_clinician_id ?? '');
    setPhysicianId(communication.physician_user_clinic_id ?? '');
    setNotes(communication.notes ?? '');
  }, [
    communication.id,
    communication.communication_date,
    communication.communication_type,
    communication.assigned_clinician_id,
    communication.physician_user_clinic_id,
    communication.notes,
  ]);

  const isDirty = useMemo(() => {
    const iso = date ? formatDateToISO(date) : null;
    const origIso = communication.communication_date;
    const origType =
      communication.communication_type ?? 'Patient Communication';
    const origPhys = communication.physician_user_clinic_id ?? '';
    return (
      iso !== origIso ||
      commType !== origType ||
      (assignee || null) !== (communication.assigned_clinician_id ?? null) ||
      physicianId !== origPhys ||
      (notes.trim() || null) !== (communication.notes?.trim() || null)
    );
  }, [date, assignee, commType, physicianId, notes, communication]);

  const handleSave = async () => {
    const d = date ? formatDateToISO(date) : null;
    if (!d) return;
    if (!notes.trim()) {
      showError('Validation', 'Please enter communication details.');
      return;
    }
    if (!physicianId) {
      showError('Validation', 'Please select a physician.');
      return;
    }
    if (needsAssignee && !assignee) {
      showError(
        'Validation',
        'Please select an assigned clinician for this type.'
      );
      return;
    }
    await onSave({
      communication_date: d,
      communication_type: commType,
      assigned_clinician_id: needsAssignee ? assignee : null,
      physician_user_clinic_id: physicianId,
      notes: notes.trim(),
    });
  };

  const saveDisabled =
    !isDirty ||
    isSaving ||
    !date ||
    !notes.trim() ||
    !physicianId ||
    (needsAssignee && !assignee);

  return (
    <>
      <div className='space-y-4'>
        <div className='space-y-1.5'>
          <Label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            Date
          </Label>
          <DatePicker date={date} onDateChange={setDate} />
        </div>
        <div className='space-y-1.5'>
          <Label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            Type
          </Label>
          <Select
            value={commType}
            onValueChange={v => setCommType(v as PatientCommunicationTypeId)}
            disabled={isSaving}
          >
            <SelectTrigger className='w-full'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PATIENT_COMMUNICATION_TYPES.map(t => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className='space-y-1.5'>
          <Label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            Physician <span className='text-destructive'>*</span>
          </Label>
          <Select
            value={physicianId || 'none'}
            onValueChange={v => setPhysicianId(v === 'none' ? '' : v)}
            disabled={isSaving}
          >
            <SelectTrigger className='w-full'>
              {physicianId ? (
                <SelectValue />
              ) : (
                <span className='text-muted-foreground'>Select physician</span>
              )}
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='none'>Select physician</SelectItem>
              {physicianOptions.map(c => (
                <SelectItem key={c.id} value={c.id}>
                  {formatClinicianName(c)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {needsAssignee && (
          <div className='space-y-1.5'>
            <Label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
              Assigned clinician <span className='text-destructive'>*</span>
            </Label>
            <Select
              value={assignee || 'none'}
              onValueChange={v => setAssignee(v === 'none' ? '' : v)}
              disabled={isSaving}
            >
              <SelectTrigger className='w-full'>
                {assignee ? (
                  <SelectValue />
                ) : (
                  <span className='text-muted-foreground'>
                    Select assigned clinician
                  </span>
                )}
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='none'>Select assigned clinician</SelectItem>
                {assignableClinicians.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {formatClinicianName(c)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className='space-y-1.5'>
          <Label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            Communication <span className='text-destructive'>*</span>
          </Label>
          <Textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder='e.g. Discussed plan with PCP Dr. Smith…'
            className='min-h-[120px] resize-y text-sm'
            disabled={isSaving}
          />
        </div>
      </div>
      <DialogFooter>
        {onCancel && (
          <Button
            type='button'
            variant='outline'
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancel
          </Button>
        )}
        <Button
          type='button'
          onClick={() => void handleSave()}
          disabled={saveDisabled}
        >
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </DialogFooter>
    </>
  );
};

export default CommunicationVisitEditor;
